/**
 * AI CONTEXT
 * Purpose: builds tryout table rows from tryouts + linked student/class/session docs.
 * Handles: cascading subscriptions, fulfillment gating, and controlled sorting.
 * Uses: semester/search/sort settings and Tryout mapper class.
 * Used by: TryoutsTable.
 * Refactor: share common "table join" utility with makeup/class hooks.
 */
import { keyBy, orderBy, uniq } from "lodash"
import { useMemo } from "react"

import { classAtoms, classSessionAtoms, tryoutAtoms } from "#atoms"
import { Tryout } from "#classes/Tryout"
import { USER_SETTINGS } from "#constants/globalConstants"
import { TRYOUT_SORTABLE_FIELDS } from "#constants/tryoutTableColumns"
import { useUserContext } from "#contexts/UserContextProvider"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { type ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import {
  type TryoutConstructor,
  type TryoutForTable,
} from "#customTypes/composedTypes/tryoutTypes"
import { SETTINGS_KEYS } from "#customTypes/globalTypes"
import {
  useIsAllFulfilled,
  useIsFulfilled,
  useTimeout,
} from "#hooks/jotai/useCascadingData"
import { useGetSomeStudents } from "#hooks/jotai/useGetStudents"
import {
  useFilteredDocs,
  useSomeDocs,
  useStableIds,
} from "#hooks/jotai/useSubscription"
import useOptimizedSearch from "#hooks/newHooks/useOptimizedSearch"
import { getSessionInfoFromId } from "#utils/classHelpers"

import { useTableSort } from "./useTableSort"

// Tryouts default to Start Date descending (JARVIS-75). Clearing the sort falls
// back to lastUpdatedAt-desc, which is the base order applied before mapping.
const DEFAULT_SORT_FIELD = "startDate"
const DEFAULT_SORT_ORDER = "descend"

// startDate displays a formatted string; sort on the numeric eventStart instead.
// result displays a tag; sort on the numeric resultRank so the order is
// Pending → Passed → Failed rather than alphabetical.
const getTryoutSortValue = (
  row: TryoutForTable,
  field: string
): string | number => {
  if (field === "startDate") return row.eventStart
  if (field === "result") return row.resultRank
  const value = row[field]
  return typeof value === "number" ? value : String(value ?? "")
}

const mapTryoutForTable = (
  tryout: TryoutConstructor,
  student: StudentConstructor,
  classObj: ClassConstructor,
  classSessionObj: ClassSessionConstructor
): TryoutForTable => {
  return new Tryout(tryout).getTryoutForTable(
    student,
    classObj,
    classSessionObj
  )
}

const TRYOUT_TABLE_FILTERS: (keyof TryoutForTable)[] = [
  "studentName",
  "studentNameAndDob",
  "studentDob",
  "className",
  "startDate",
] as const

const useTryoutTableData = () => {
  const { getUserSetting } = useUserContext()
  const filterSemester = getUserSetting<string>(
    USER_SETTINGS.TRYOUTS_TABLE_SEMESTER
  )
  const resultsCount = getUserSetting<number>(
    USER_SETTINGS.TRYOUTS_TABLE_RESULTS_COUNT
  )
  const defaultSearchTerm = getUserSetting<string>(
    USER_SETTINGS.TRYOUTS_TABLE_SEARCH_TERM
  )

  const { sortField, sortOrder, sortRows, onSortChange } =
    useTableSort<TryoutForTable>({
      tableKey: SETTINGS_KEYS.TRYOUTS,
      sortableFields: TRYOUT_SORTABLE_FIELDS,
      defaultField: DEFAULT_SORT_FIELD,
      defaultOrder: DEFAULT_SORT_ORDER,
      getSortValue: getTryoutSortValue,
    })

  const queryConfig = useMemo<FirebaseQueryConfig>(() => {
    if (filterSemester === "All") return {}

    return {
      queries: {
        semester: {
          value: filterSemester,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }
  }, [filterSemester])

  const tryoutsFromDb = useFilteredDocs<TryoutConstructor>(
    tryoutAtoms,
    DOC_TYPES.TRYOUTS,
    queryConfig
  )

  const tryoutsFetched = useTimeout(3000, tryoutsFromDb.length > 0)

  const studentIds = useMemo(() => {
    if (!tryoutsFetched) return []
    const studentIds = tryoutsFromDb.map((tryout) => tryout.studentId)
    return uniq(studentIds)
  }, [tryoutsFetched, tryoutsFromDb])

  const stableStudentIds = useStableIds(studentIds)
  const students = useGetSomeStudents(stableStudentIds)

  const studentsById = useMemo(() => {
    return keyBy(students, "id")
  }, [students])

  const classIds = useMemo(() => {
    if (!tryoutsFetched) return []
    const classIds = tryoutsFromDb.map((tryout) => {
      const [classId] = getSessionInfoFromId(tryout.sessionId)
      return classId
    })
    return uniq(classIds)
  }, [tryoutsFetched, tryoutsFromDb])

  const stableClassIds = useStableIds(classIds)

  const classes = useSomeDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    stableClassIds
  )

  const classesById = useMemo(() => {
    return keyBy(classes, "id")
  }, [classes])

  const tryoutSessionIds = useMemo(() => {
    if (!tryoutsFetched) return []
    const sessionIds = tryoutsFromDb.map((tryout) => tryout.sessionId)
    return uniq(sessionIds)
  }, [tryoutsFetched, tryoutsFromDb])

  const stableTryoutSessionIds = useStableIds(tryoutSessionIds)

  const classSessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    stableTryoutSessionIds
  )

  const classSessionsById = useMemo(() => {
    return keyBy(classSessions, "id")
  }, [classSessions])

  // Track fulfillment

  const studentsFulfilled = useIsFulfilled(stableStudentIds, students, 7000)
  const classesFulfilled = useIsFulfilled(stableClassIds, classes, 7000)
  const sessionsFulfilled = useIsFulfilled(
    stableTryoutSessionIds,
    classSessions,
    7000
  )

  const allFulfilled = useIsAllFulfilled([
    tryoutsFetched,
    studentsFulfilled,
    classesFulfilled,
    sessionsFulfilled,
  ])

  // Map in lastUpdatedAt-desc base order, then apply the controlled column sort
  // BEFORE truncation so the visible rows reflect the global order. When no
  // column sort is active, sortRows is a no-op and the base order is kept.
  const sortedRows = useMemo(() => {
    if (!tryoutsFromDb?.length || !allFulfilled) return []

    const rows = orderBy(tryoutsFromDb, "lastUpdatedAt", "desc")
      .map((tryout) => {
        const student = studentsById[tryout.studentId]
        const [classId] = getSessionInfoFromId(tryout.sessionId)
        const classObj = classesById[classId]
        const classSessionObj = classSessionsById[tryout.sessionId]

        // Show placeholder if data hasn't loaded yet
        if (!student || !classObj || !classSessionObj) {
          return null
        }

        const row = mapTryoutForTable(
          tryout,
          student,
          classObj,
          classSessionObj
        )
        row.lastUpdatedAt = tryout.lastUpdatedAt
        return row
      })
      .filter((t): t is TryoutForTable => t !== null)

    return sortRows(rows)
  }, [
    tryoutsFromDb,
    studentsById,
    classesById,
    classSessionsById,
    allFulfilled,
    sortRows,
  ])

  const { filteredItems: filteredTryouts, onSearch } = useOptimizedSearch(
    sortedRows,
    TRYOUT_TABLE_FILTERS,
    resultsCount,
    defaultSearchTerm
  )

  // Re-apply the sort after fuzzy search so display order stays consistent.
  const tryoutsForTable = useMemo(
    () => sortRows(filteredTryouts),
    [sortRows, filteredTryouts]
  )

  return {
    tryoutsForTable,
    onSearch,
    isReady: allFulfilled,
    errors: [], // Errors are handled at the subscription level
    sortField,
    sortOrder,
    onSortChange,
  }
}

export default useTryoutTableData
